// Excel import / export for the estimating database pages.
//
// Materials, Labor and Tools keep the desktop's sheet layouts
// (MaterialsDatabaseViewModel / LaborDatabaseViewModel /
// ToolsDatabaseViewModel ExportData + ImportData), so a file exported from
// either client imports into the other. The simple pages, which the desktop
// cannot export, use one header row of field labels.
//
// Money is written in the version's currency (stored value x rate) and read
// back to USD (value / rate), the way the desktop's labour and equipment
// sheets do it.

import * as XLSX from "xlsx";
import { YIELD_FIELDS, yieldUnitLabel } from "./databaseUnits";

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
// The desktop sheets write money as double.Parse(string.Format("{0:n}", cost))
// * rate: the stored USD cost rounded to the cent, THEN converted.
const deskMoney = (v, rate) => round2(v) * (rate || 1);
const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const str = (v) => (v === null || v === undefined ? "" : String(v).trim());
const stripTree = (v) => str(v).replace(/^[|_\s]+/, "");

export function readWorkbook(data) {
  return XLSX.read(data, { type: "array" });
}

export function downloadWorkbook(workbook, fileName) {
  XLSX.writeFile(workbook, fileName);
}

function sheetRows(workbook, preferredName) {
  const name =
    (preferredName && workbook.SheetNames.find((n) => n.toLowerCase() === preferredName.toLowerCase())) ||
    workbook.SheetNames[0];
  const ws = workbook.Sheets[name];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
}

const exportValue = (f, rec, rate) => {
  const v = rec[f.key];
  if (f.type === "number") return f.currency ? round2(toNumber(v) * rate) : toNumber(v);
  if (Array.isArray(v)) return v.join(",");
  return v === undefined || v === null ? "" : v;
};

// ---- simple pages -------------------------------------------------------

export function buildFlatWorkbook(sheetName, fields, recs, { rate = 1 } = {}) {
  const cols = fields.filter((f) => f.type !== "readonly");
  const aoa = [cols.map((f) => f.label)];
  recs.forEach((rec) => aoa.push(cols.map((f) => exportValue(f, rec, rate))));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), sheetName.slice(0, 31));
  return wb;
}

// Columns are found by their label (or field key), in any order.
export function readFlatWorkbook(workbook, sheetName, fields, { rate = 1 } = {}) {
  const rows = sheetRows(workbook, sheetName);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => str(h).toLowerCase());
  const index = {};
  fields.forEach((f) => {
    const i = header.findIndex((h) => h === f.label.toLowerCase() || h === f.key.toLowerCase());
    if (i >= 0) index[f.key] = i;
  });
  return rows.slice(1).map((row) => {
    const rec = {};
    fields.forEach((f) => {
      if (index[f.key] === undefined) return;
      const raw = row[index[f.key]];
      if (f.type === "number") {
        rec[f.key] = f.currency ? toNumber(raw) / rate : toNumber(raw);
      } else {
        rec[f.key] = str(raw);
      }
    });
    return rec;
  }).filter((rec) => Object.values(rec).some((v) => v !== "" && v !== 0));
}

// ---- materials (desktop layout) -----------------------------------------

const QUANTS = ["SF", "LF", "CY", "SY", "TON", "LB", "EA"];

export function buildMaterialsWorkbook(recs, { rate = 1, materialClasses = [] } = {}) {
  const aoa = [
    ["Name", ...QUANTS.map((u) => `Quant / ${u}`), "Unit Cost", "Unit", "Material Class"],
  ];
  recs.forEach((r) =>
    aoa.push([
      r.Code,
      ...QUANTS.map((u) => toNumber(r[`Quant${u}`])),
      deskMoney(r.UnitCost, rate),
      r.Unit || "SF",
      r.MaterialClass || "",
    ])
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Materials");
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet([["Material Class"], ...materialClasses.map((c) => [c])]),
    "MaterialClass"
  );
  return wb;
}

export function readMaterialsWorkbook(workbook, { rate = 1 } = {}) {
  const rows = sheetRows(workbook, "Materials");
  return rows
    .slice(1)
    .filter((r) => str(r[0]))
    .map((r) => {
      const rec = { Code: str(r[0]) };
      QUANTS.forEach((u, i) => {
        rec[`Quant${u}`] = toNumber(r[1 + i]);
      });
      rec.UnitCost = toNumber(r[8]) / rate;
      rec.Unit = str(r[9]) || "SF";
      rec.MaterialClass = str(r[10]);
      return rec;
    });
}

// ---- labor (desktop layout) ---------------------------------------------
// Row 1: Code | Description | Labor Properties (C:D) | Crew Properties (E:L)
// Row 2: Percentage, Unit Cost, Yield / <unit> x7, AWP
// Row 3+: a crew row, then its labours as "|_<code>" / "|_<description>".

export function buildLaborWorkbook(crews, { rate = 1, metric = false } = {}) {
  const aoa = [
    ["Code", "Description", "Labor Properties", "", "Crew Properties", "", "", "", "", "", "", ""],
    ["", "", "Percentage", "Unit Cost", ...YIELD_FIELDS.map((u) => `Yield / ${yieldUnitLabel(u, metric)}`), "AWP"],
  ];
  crews.forEach(({ crew, labors }) => {
    aoa.push([
      crew.Code,
      crew.Description,
      "",
      "",
      ...YIELD_FIELDS.map((u) => toNumber(crew[`Yield${u}`])),
      (crew.Awp || []).join(","),
    ]);
    (labors || []).forEach((l) =>
      aoa.push([`|_${l.Code}`, `|_${l.Description}`, toNumber(l.Percentage), deskMoney(l.UnitCost, rate)])
    );
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
    { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
    { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } },
    { s: { r: 0, c: 4 }, e: { r: 0, c: 11 } },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Labor");
  return wb;
}

// Returns [{ crew, labors }]; a labour row ("x.y" code) belongs to the crew
// row above it.
export function readLaborWorkbook(workbook, { rate = 1 } = {}) {
  const rows = sheetRows(workbook, "Labor");
  const out = [];
  let current = null;
  rows.slice(2).forEach((r) => {
    const rawCode = str(r[0]);
    if (!rawCode) return;
    const code = stripTree(rawCode);
    if (code.includes(".")) {
      const labor = {
        Code: code,
        Description: stripTree(r[1]),
        Percentage: toNumber(r[2]),
        UnitCost: toNumber(r[3]) / rate,
      };
      if (current) current.labors.push(labor);
      else out.push({ crew: null, labors: [labor] });
      return;
    }
    const crew = {
      Code: code,
      Description: str(r[1]),
      Awp: str(r[11])
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    YIELD_FIELDS.forEach((u, i) => {
      crew[`Yield${u}`] = toNumber(r[4 + i]);
    });
    current = { crew, labors: [] };
    out.push(current);
  });
  return out;
}

// ---- tools (desktop layout) ---------------------------------------------
// Row 1 headers; then "-" | <category>, "|_<group code>" | "|_<type>",
// "|  |_<tool code>" | "|  |_<description>" | unit cost | unit | yields | AWP.

export function buildToolsWorkbook(categories, { rate = 1, metric = false } = {}) {
  const aoa = [
    [
      "Code",
      "Description",
      "Unit Cost",
      "Unit",
      ...YIELD_FIELDS.map((u) => `Yield / ${yieldUnitLabel(u, metric)}`),
      "AWP",
    ],
  ];
  categories.forEach(({ category, groups }) => {
    aoa.push(["-", category]);
    (groups || []).forEach(({ group, tools }) => {
      aoa.push([`|_${group.Code}`, `|_${group.Type || ""}`]);
      (tools || []).forEach((t) =>
        aoa.push([
          `|  |_${t.Code}`,
          `|  |_${t.Description || ""}`,
          deskMoney(t.UnitCost, rate),
          t.Unit || "",
          ...YIELD_FIELDS.map((u) => toNumber(t[`Yield${u}`])),
          (t.Awp || []).join(","),
        ])
      );
    });
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Equipment");
  return wb;
}

// Returns [{ category, groups: [{ group, tools }] }].
export function readToolsWorkbook(workbook, { rate = 1 } = {}) {
  const rows = sheetRows(workbook, "Equipment");
  const out = [];
  let cat = null;
  let grp = null;
  rows.slice(1).forEach((r) => {
    const rawCode = str(r[0]);
    if (!rawCode && !str(r[1])) return;
    if (rawCode.startsWith("|  |_") || rawCode.startsWith("| |_")) {
      const tool = {
        Code: stripTree(rawCode),
        Description: stripTree(r[1]),
        UnitCost: toNumber(r[2]) / rate,
        Unit: str(r[3]),
        Awp: str(r[11])
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      YIELD_FIELDS.forEach((u, i) => {
        tool[`Yield${u}`] = toNumber(r[4 + i]);
      });
      if (grp) grp.tools.push(tool);
      return;
    }
    if (rawCode.startsWith("|_")) {
      grp = {
        group: { Code: stripTree(rawCode), Type: stripTree(r[1]), Category: cat ? cat.category : "" },
        tools: [],
      };
      if (!cat) {
        cat = { category: "", groups: [] };
        out.push(cat);
      }
      cat.groups.push(grp);
      return;
    }
    cat = { category: str(r[1]), groups: [] };
    grp = null;
    out.push(cat);
  });
  return out;
}
